import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import type { IndicatorSeries, RangeKey } from "../types";
import type { HousingRtmsBandDefinition, HousingRtmsBandRow, HousingRtmsScopeId } from "../data/adapters/housingRtms";
import {
  HOUSING_REGION_PROFILES,
  directRateSensitivityProxy,
  equityOpportunityCostAnnualKrw,
  maxMortgageCapacity,
  type HousingPolicyBundle,
  type HousingRegionProfileId
} from "../data/housingPolicyRegimes";
import { latestChange, latestValue } from "../utils/series";
import { monthlyAverageSeries } from "../data/housingTransmissionSeries";
import { rtmsBandSeries } from "../data/housingChartSeries";
import TimeSeriesChart from "./TimeSeriesChart";
import LegacyMortgageStressSection from "./LegacyMortgageStressSection";
import type { LegacyMortgageStressBundle } from "../data/legacyMortgageStress";
import type { HousingLeadLagScan } from "../data/housingLeadLag";
import LagAnalysisTable from "./LagAnalysisTable";
import HousingRelationshipAnalysisPanel from "./HousingRelationshipAnalysisPanel";
import TransmissionHypothesisPanel from "./TransmissionHypothesisPanel";
import HousingMarketTechnicalChart from "./HousingMarketTechnicalChart";
import PublicHousingHtsShareTools from "./PublicHousingHtsShareTools";
import type { HousingRelationshipAnalysis } from "../data/housingRelationshipAnalysis";
import type { HistoricalCoverageRegistry } from "../data/historicalCoverage";

const PROFILE_ORDER: HousingRegionProfileId[] = ["capital_regulated", "capital_nonregulated"];

const HOUSING_BAND_COLORS: Record<string, string> = {
  p1_le_15: "#2563eb",
  p2_15_25: "#7c3aed",
  p3_gt_25: "#ea580c",
  m1_le_15: "#2563eb",
  m2_15_30: "#7c3aed",
  m3_30_50: "#ea580c",
  m4_ge_50: "#b91c1c",
  t1_ge_60: "#7f1d1d"
};

const HOUSING_SCOPE_LABELS: Record<HousingRtmsScopeId, string> = {
  national: "전국",
  capital: "수도권",
  noncapital: "비수도권",
  seoul: "서울",
  gyeonggi: "경기",
  incheon: "인천",
  gangnam3: "강남3구",
  prime4: "강남3구+용산",
  seoul_non_gangnam: "서울 비강남3구"
};

const MARKET_SCOPE_ORDER: HousingRtmsScopeId[] = ["prime4", "gangnam3", "seoul", "seoul_non_gangnam", "capital"];

function previousMonth(month: string) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function eokFromKrw(value: number | null) {
  return value == null ? "N/A" : `${(value / 100_000_000).toFixed(1)}억`;
}

function signedPct(value: number | null) {
  if (value == null || !Number.isFinite(value)) return "N/A";
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function eok(value: number | null, digits = 1) {
  return value == null ? "N/A" : `${(value / 100_000_000).toFixed(digits)}억`;
}

function pct(value: number | null, digits = 1) {
  return value == null ? "N/A" : `${(value * 100).toFixed(digits)}%`;
}

function annualEok(value: number | null) {
  return value == null ? "N/A" : `${(value / 100_000_000).toFixed(2)}억원/년`;
}

function bindingLabel(value: string) {
  if (value === "absolute_cap") return "가격대별 절대한도";
  if (value === "ltv") return "LTV";
  if (value === "dsr") return "DSR";
  if (value === "multiple") return "복수 제약 동시";
  return "확인 불가";
}

type DetailRequest = (spec: { title: string; description: string; series: IndicatorSeries[]; referenceZero?: boolean }) => void;

export default function HousingTransmissionSection({
  housing,
  mortgageRate,
  fundingRate,
  benchmarkRate,
  legacyMortgage,
  housingLagScans,
  housingRelationshipAnalysis,
  officialHousingPrices,
  historicalCoverage,
  range,
  onOpenDetail
}: {
  housing: HousingPolicyBundle;
  mortgageRate: IndicatorSeries;
  fundingRate: IndicatorSeries;
  benchmarkRate: IndicatorSeries;
  legacyMortgage: LegacyMortgageStressBundle;
  housingLagScans: HousingLeadLagScan[];
  housingRelationshipAnalysis: HousingRelationshipAnalysis;
  officialHousingPrices: IndicatorSeries[];
  historicalCoverage: HistoricalCoverageRegistry;
  range: RangeKey;
  onOpenDetail: DetailRequest;
}) {
  const [profileId, setProfileId] = useState<HousingRegionProfileId>("capital_regulated");
  const [rtmsScope, setRtmsScope] = useState<HousingRtmsScopeId>("seoul");
  const [rtmsMetric, setRtmsMetric] = useState<"count" | "share">("count");
  const [rtmsSmoothing, setRtmsSmoothing] = useState<"raw" | "ma3">("raw");
  const [marketScope, setMarketScope] = useState<HousingRtmsScopeId>("prime4");
  const [marketMetric, setMarketMetric] = useState<"count" | "share">("count");
  const [marketSmoothing, setMarketSmoothing] = useState<"raw" | "ma3">("ma3");
  const profile = HOUSING_REGION_PROFILES.find((x) => x.id === profileId) ?? HOUSING_REGION_PROFILES[0];
  const selected = housing.profiles.find((x) => x.region.id === profile.id);
  const curve = selected?.curve ?? [];
  const regime = selected?.regime ?? housing.activeRegime;
  const mortgageChange = latestChange(mortgageRate);
  const benchmarkYield = latestValue(benchmarkRate);

  const examples = useMemo(() => housing.examplePricesEok.map((priceEok) => {
    const result = maxMortgageCapacity({
      date: housing.asOfDate,
      region: profile,
      homePriceKrw: priceEok * 100_000_000
    });
    const sensitivity = directRateSensitivityProxy(result.capacityRatio, mortgageChange);
    const opportunity = equityOpportunityCostAnnualKrw(result.equityRequiredKrw, benchmarkYield);
    return { priceEok, result, sensitivity, opportunity };
  }), [housing.asOfDate, housing.examplePricesEok, profile, mortgageChange, benchmarkYield]);

  const bandCards = regime?.bands ?? [];
  const rtms = housing.transactionModule.rtms;
  const rtmsRows = rtms?.scopes[rtmsScope] ?? [];
  const rtmsBands = rtms?.analysisBands ?? [
    { id: "p1_le_15", label: "15억원 이하" },
    { id: "p2_15_25", label: "15억원 초과~25억원 이하" },
    { id: "p3_gt_25", label: "25억원 초과" }
  ];

  const market = rtms?.marketAnalysis;
  const marketBands = market?.bands ?? [];
  const marketRows = market?.scopes[marketScope] ?? [];
  const benchmarkMonthly = useMemo(() => monthlyAverageSeries(benchmarkRate, { id: "housing_h4_ktb5_monthly", name: "국고채 5년 월평균", shortName: "KTB5Y 월평균" }), [benchmarkRate]);
  const fundingMonthly = useMemo(() => monthlyAverageSeries(fundingRate, { id: "housing_h4_funding_monthly", name: "금융채 신규조달금리 월평균", shortName: "금융채 신규조달" }), [fundingRate]);
  const housingRateSeries = useMemo(() => [benchmarkMonthly, fundingMonthly, mortgageRate], [benchmarkMonthly, fundingMonthly, mortgageRate]);
  const rtmsActivitySeries = useMemo(() => rtmsBandSeries({
    rows: rtmsRows, bands: rtmsBands, scope: rtmsScope, scopeLabel: HOUSING_SCOPE_LABELS[rtmsScope],
    status: rtms?.status ?? "not_collected", metric: rtmsMetric, smoothing: rtmsSmoothing, idPrefix: `housing_rtms_${rtmsScope}`,
    dataset: "아파트 매매 실거래 가격대별 집계", generatedAt: rtms?.generatedAt
  }), [rtmsRows, rtmsBands, rtmsScope, rtms?.status, rtms?.generatedAt, rtmsMetric, rtmsSmoothing]);
  const rtmsPriceSeries = useMemo(() => rtmsBandSeries({
    rows: rtmsRows, bands: rtmsBands, scope: rtmsScope, scopeLabel: HOUSING_SCOPE_LABELS[rtmsScope],
    status: rtms?.status ?? "not_collected", metric: "price", idPrefix: `housing_rtms_${rtmsScope}`,
    dataset: "아파트 매매 실거래 가격대별 집계", generatedAt: rtms?.generatedAt
  }), [rtmsRows, rtmsBands, rtmsScope, rtms?.status, rtms?.generatedAt]);
  const rtmsCountSeries = useMemo(() => rtmsBandSeries({
    rows: rtmsRows, bands: rtmsBands, scope: rtmsScope, scopeLabel: HOUSING_SCOPE_LABELS[rtmsScope],
    status: rtms?.status ?? "not_collected", metric: "count", idPrefix: `housing_rtms_h4_${rtmsScope}`,
    dataset: "아파트 매매 실거래 가격대별 집계", generatedAt: rtms?.generatedAt
  }), [rtmsRows, rtmsBands, rtmsScope, rtms?.status, rtms?.generatedAt]);
  const marketActivitySeries = useMemo(() => rtmsBandSeries({
    rows: marketRows, bands: marketBands, scope: marketScope, scopeLabel: HOUSING_SCOPE_LABELS[marketScope],
    status: market?.status ?? "not_collected", metric: marketMetric, smoothing: marketSmoothing, idPrefix: `housing_market_${marketScope}`,
    dataset: "아파트 고가시장 30/50억 구간 집계", generatedAt: rtms?.generatedAt
  }), [marketRows, marketBands, marketScope, market?.status, marketMetric, marketSmoothing, rtms?.generatedAt]);
  const marketPriceSeries = useMemo(() => rtmsBandSeries({
    rows: marketRows, bands: marketBands, scope: marketScope, scopeLabel: HOUSING_SCOPE_LABELS[marketScope],
    status: market?.status ?? "not_collected", metric: "price", idPrefix: `housing_market_${marketScope}`,
    dataset: "아파트 고가시장 30/50억 구간 집계", generatedAt: rtms?.generatedAt, suppressThinPriceBelow: 5
  }), [marketRows, marketBands, marketScope, market?.status, rtms?.generatedAt]);

  const rtmsLatest = useMemo(() => {
    const byKey = new Map(rtmsRows.map((row) => [`${row.month}:${row.bandId}`, row]));
    const availableMonths = [...new Set(rtmsRows.map((r) => r.month))].sort();
    const configuredFullMonth = rtms?.coverage.latestFullCalendarMonth ?? null;
    const latestMonth = configuredFullMonth && availableMonths.includes(configuredFullMonth)
      ? configuredFullMonth
      : availableMonths.filter((m) => !rtms?.coverage.currentMonthProvisional || m !== rtms?.coverage.endMonth).at(-1) ?? availableMonths.at(-1) ?? null;
    return rtmsBands.map((band) => {
      const row = latestMonth ? byKey.get(`${latestMonth}:${band.id}`) ?? null : null;
      const prev = latestMonth ? byKey.get(`${previousMonth(latestMonth)}:${band.id}`) ?? null : null;
      const countMom = row && prev && prev.transactionCount > 0 ? (row.transactionCount / prev.transactionCount - 1) * 100 : null;
      const priceMom = row?.medianPriceProxyKrw != null && prev?.medianPriceProxyKrw != null && prev.medianPriceProxyKrw > 0
        ? (row.medianPriceProxyKrw / prev.medianPriceProxyKrw - 1) * 100 : null;
      return { band, row, prev, countMom, priceMom };
    });
  }, [rtmsRows, rtmsBands, rtms?.coverage.latestFullCalendarMonth, rtms?.coverage.currentMonthProvisional, rtms?.coverage.endMonth]);

  const marketLatest = useMemo(() => {
    const availableMonths = [...new Set(marketRows.map((row) => row.month))].sort();
    const latestMonth = availableMonths.filter((m) => !rtms?.coverage.currentMonthProvisional || m !== rtms?.coverage.endMonth).at(-1) ?? availableMonths.at(-1) ?? null;
    const byKey = new Map(marketRows.map((row) => [`${row.month}:${row.bandId}`, row]));
    return {
      month: latestMonth,
      bands: marketBands.map((band) => {
        const row = latestMonth ? byKey.get(`${latestMonth}:${band.id}`) ?? null : null;
        const prev = latestMonth ? byKey.get(`${previousMonth(latestMonth)}:${band.id}`) ?? null : null;
        const countMom = row && prev && prev.transactionCount > 0 ? (row.transactionCount / prev.transactionCount - 1) * 100 : null;
        const priceMom = row?.medianPriceProxyKrw != null && prev?.medianPriceProxyKrw != null && prev.medianPriceProxyKrw > 0 && row.transactionCount >= 5 && prev.transactionCount >= 5
          ? (row.medianPriceProxyKrw / prev.medianPriceProxyKrw - 1) * 100
          : null;
        return { band, row, countMom, priceMom };
      })
    };
  }, [marketRows, marketBands, rtms?.coverage.currentMonthProvisional, rtms?.coverage.endMonth]);

  const primeSnapshot = useMemo(() => {
    if (!market) return null;
    const monthSets = ["seoul", "gangnam3", "prime4"].map((scope) => new Set((market.scopes[scope as HousingRtmsScopeId] ?? []).map((row) => row.month)));
    const candidateMonths = [...monthSets[0]].filter((month) => monthSets.slice(1).every((set) => set.has(month))).sort();
    const month = candidateMonths.filter((m) => !rtms?.coverage.currentMonthProvisional || m !== rtms?.coverage.endMonth).at(-1) ?? candidateMonths.at(-1) ?? null;
    if (!month) return null;
    const count = (scope: HousingRtmsScopeId, ids: string[]) => (market.scopes[scope] ?? [])
      .filter((row) => row.month === month && ids.includes(row.bandId))
      .reduce((sum, row) => sum + row.transactionCount, 0);
    const tail60 = (market.tailScopes.prime4 ?? []).find((row) => row.month === month)?.transactionCount ?? 0;
    const seoulTotal = count("seoul", market.bands.map((band) => band.id));
    const gangnam30 = count("gangnam3", ["m3_30_50", "m4_ge_50"]);
    const prime50 = count("prime4", ["m4_ge_50"]);
    const prime30 = count("prime4", ["m3_30_50", "m4_ge_50"]);
    return {
      month,
      gangnam30,
      prime50,
      prime60: tail60,
      primeSharePct: seoulTotal > 0 ? prime30 / seoulTotal * 100 : null
    };
  }, [market, rtms?.coverage.currentMonthProvisional, rtms?.coverage.endMonth]);

  return (
    <div className="housing-module">
      <div className="housing-module__toolbar">
        <div>
          <p className="kicker">POLICY REGIME · {housing.asOfDate}</p>
          <h3>주택가격대별 금리전이</h3>
          <p>주택 전체를 한 덩어리로 보지 않고, 정책상 가능한 레버리지와 금리경로를 가격대별로 분리합니다.</p>
        </div>
        <div className="housing-control-block housing-control-block--end">
          <span>정책 지역</span>
          <div className="housing-profile-switch" role="group" aria-label="주택정책 비교 지역">
            {PROFILE_ORDER.map((id) => {
              const item = HOUSING_REGION_PROFILES.find((x) => x.id === id)!;
              return <button key={id} type="button" className={profileId === id ? "is-active" : ""} onClick={() => setProfileId(id)}>{item.label}</button>;
            })}
          </div>
        </div>
      </div>

      <div className="housing-policy-note">
        <strong>{regime?.sourceTitle ?? "적용 정책 레짐 없음"}</strong>
        <span>{regime?.note ?? "해당 날짜·지역 조합에 등록된 정책 레짐이 없습니다."}</span>
        {regime && <a href={regime.sourceUrl} target="_blank" rel="noreferrer">공식 근거</a>}
      </div>

      <div className="housing-policy-timeline">
        {housing.regimes.map((item) => (
          <article key={item.id} className={item.id === housing.activeRegimeId ? "is-active" : ""}>
            <span>{item.effectiveFrom}{item.effectiveTo ? ` ~ ${item.effectiveTo}` : " ~ 현재"}</span>
            <strong>{item.id === "fsc-2025-06-28-capital-purchase-cap-600m" ? "수도권·규제지역 6억원 단일한도" : "가격대별 6/4/2억원 차등한도"}</strong>
            <small>{item.sourceTitle}</small>
          </article>
        ))}
      </div>

      <div className="housing-band-grid">
        {bandCards.map((band, index) => {
          const examplePriceEok = housing.examplePricesEok[Math.min(index, housing.examplePricesEok.length - 1)];
          const result = maxMortgageCapacity({ date: housing.asOfDate, region: profile, homePriceKrw: examplePriceEok * 100_000_000 });
          return (
            <article className="housing-band-card" key={band.id}>
              <span className="housing-band-card__index">P{index + 1}</span>
              <div>
                <h4>{band.label}</h4>
                <strong>절대한도 {eok(band.mortgageCapKrw ?? null, 0)}</strong>
                <p>예시 {examplePriceEok}억원 주택 → 알려진 규제 기준 최대 {eok(result.allowedMortgageKrw, 1)} / 가격 대비 {pct(result.capacityRatio)}</p>
                <small>현재 예시의 결박 제약: {bindingLabel(result.bindingConstraint)} · DSR은 차주정보가 없어 미반영</small>
              </div>
            </article>
          );
        })}
      </div>

      <div className="housing-grid">
        <section className="housing-chart-card">
          <header>
            <div>
              <h4>정책상 최대 대출비중 곡선</h4>
              <p>x=주택가격, y=알려진 LTV·절대한도 중 더 낮은 값 ÷ 주택가격. 실제 평균대출액이 아닙니다.</p>
            </div>
          </header>
          <div className="housing-capacity-chart">
            <ResponsiveContainer width="100%" height={310}>
              <LineChart data={curve} margin={{ top: 12, right: 18, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis type="number" dataKey="priceEok" domain={[1, 40]} tick={{ fontSize: 10 }} tickFormatter={(v: unknown) => `${String(v)}억`} />
                <YAxis type="number" domain={[0, 80]} tick={{ fontSize: 10 }} tickFormatter={(v: unknown) => `${String(v)}%`} width={46} />
                <Tooltip formatter={(value: unknown, name: unknown) => [String(name) === "capacityPct" ? `${Number(value).toFixed(1)}%` : String(value), String(name) === "capacityPct" ? "최대 대출비중" : String(name)]} labelFormatter={(label: unknown) => `주택가격 ${String(label)}억원`} />
                <ReferenceLine x={15} stroke="#94a3b8" strokeDasharray="4 4" />
                <ReferenceLine x={25} stroke="#94a3b8" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="capacityPct" stroke="#2563eb" strokeWidth={2.2} dot={false} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="housing-thresholds"><span>15억: 6억 → 4억 경계</span><span>25억: 4억 → 2억 경계</span></div>
        </section>

        <section className="housing-example-card">
          <header>
            <h4>금리 경로를 가격대별로 읽는 예시</h4>
            <p>실거래 통계가 붙기 전 H1 단계의 정책 노출 계산입니다. 현재 주담대 변화와 KTB5Y를 참고하되 가격 예측으로 사용하지 않습니다.</p>
          </header>
          <div className="housing-example-table-wrap">
            <table className="housing-example-table">
              <thead><tr><th>예시가격</th><th>정책상 최대대출</th><th>대출비중</th><th>필요 자기자본</th><th>직접 금리노출 proxy</th><th>자기자본 기회비용*</th></tr></thead>
              <tbody>
                {examples.map(({ priceEok, result, sensitivity, opportunity }) => (
                  <tr key={priceEok}>
                    <td><strong>{priceEok}억</strong></td>
                    <td>{eok(result.allowedMortgageKrw)}</td>
                    <td>{pct(result.capacityRatio)}</td>
                    <td>{eok(result.equityRequiredKrw)}</td>
                    <td>{sensitivity == null ? "N/A" : sensitivity.toFixed(3)}</td>
                    <td>{annualEok(opportunity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <small>* 자기자본 기회비용 = 필요한 자기자본 × 현재 KTB5Y. 실제 투자수익·주거가치·세금·임대수익을 포함한 손익이 아닙니다.</small>
        </section>
      </div>

      <div className="housing-interpretation-grid">
        <article><span className="housing-evidence housing-evidence--observed">관측</span><strong>현재 정책은 가격구간에 따라 절대한도가 계단식으로 낮아집니다.</strong><p>수도권·규제지역 주택구입목적 주담대는 15억원 이하 6억원, 15~25억원 4억원, 25억원 초과 2억원의 절대한도가 적용됩니다.</p></article>
        <article><span className="housing-evidence housing-evidence--mechanism">메커니즘</span><strong>고가주택일수록 대출이자 직접경로가 상대적으로 약해질 수 있습니다.</strong><p>가격 대비 허용 가능한 대출비중이 작아지면 주담대금리 변화가 구매자 전체 자금에 직접 걸리는 비중도 낮아질 수 있습니다.</p></article>
        <article><span className="housing-evidence housing-evidence--inference">해석</span><strong>그렇다고 고가주택이 금리에 무관한 것은 아닙니다.</strong><p>필요 자기자본이 커지므로 국채·현금성 자산 수익률이 오를 때 자기자본의 기회비용 채널은 오히려 커질 수 있습니다. 이는 가격 방향 판정이 아니라 해석 가설입니다.</p></article>
      </div>

      <LegacyMortgageStressSection legacy={legacyMortgage} housing={housing} fundingRate={fundingRate} benchmarkRate={benchmarkRate} range={range} onOpenDetail={onOpenDetail} />

      <section className="housing-h2-section">
        <div className="housing-h2-header">
          <div>
            <p className="kicker">H2 · MOLIT RTMS APARTMENT TRADES</p>
            <h4>가격대별 실제 거래 반응</h4>
            <p>{housing.transactionModule.note}</p>
          </div>
          <div className="housing-h2-controls">
            <div className="housing-control-block">
              <span>지역</span>
              <div className="housing-profile-switch" role="group" aria-label="실거래 지역범위">
                {(Object.keys(HOUSING_SCOPE_LABELS) as HousingRtmsScopeId[]).map((id) => (
                  <button key={id} type="button" className={rtmsScope === id ? "is-active" : ""} onClick={() => setRtmsScope(id)}>{HOUSING_SCOPE_LABELS[id]}</button>
                ))}
              </div>
            </div>
            <div className="housing-control-block">
              <span>표시</span>
              <div className="housing-profile-switch" role="group" aria-label="거래량 표시방식">
                <button type="button" className={rtmsMetric === "count" ? "is-active" : ""} onClick={() => setRtmsMetric("count")}>건수</button>
                <button type="button" className={rtmsMetric === "share" ? "is-active" : ""} onClick={() => setRtmsMetric("share")}>비중</button>
              </div>
            </div>
            <div className="housing-control-block">
              <span>평활화</span>
              <div className="housing-profile-switch" role="group" aria-label="거래량 평활화">
                <button type="button" className={rtmsSmoothing === "raw" ? "is-active" : ""} onClick={() => setRtmsSmoothing("raw")}>원자료</button>
                <button type="button" className={rtmsSmoothing === "ma3" ? "is-active" : ""} onClick={() => setRtmsSmoothing("ma3")}>3M 평균</button>
              </div>
            </div>
          </div>
        </div>

        {!rtms || housing.transactionModule.status === "not_connected" ? (
          <div className="housing-h2-placeholder">
            <strong>H2 실거래 연결 상태: N/A</strong>
            <span>{housing.transactionModule.note}</span>
            <small>SDP의 RTMS 중앙 수집과 이관 snapshot을 읽을 수 있을 때까지 실제 거래반응을 표시하지 않습니다.</small>
          </div>
        ) : (
          <>
            <div className="housing-h2-status">
              <span className={`data-pill data-pill--${housing.transactionModule.status === "ready" ? "ok" : "warn"}`}>{housing.transactionModule.status.toUpperCase()}</span>
              <span>범위 {rtms.coverage.startMonth ?? "N/A"} ~ {rtms.coverage.endMonth ?? "N/A"}</span>
              <span>수집 {rtms.coverage.completedCombinations}/{rtms.coverage.expectedCombinations} 지역×월</span>
              {rtms.centralNative && <span>SDP 신규구간 {rtms.centralNative.status === "merged" ? `${rtms.centralNative.completeMonths?.join(", ") || "병합"}` : rtms.centralNative.status === "waiting_for_complete_month" ? "전체 지역 수집 중" : rtms.centralNative.status === "unavailable" ? "조회 실패 · 이관본 유지" : "추가 완전월 없음"}</span>}
              {rtms.coverage.verification ? <>
                <span>source-raw {rtms.coverage.verification.sourceArchiveCombinations}/{rtms.coverage.verification.eligibleCombinations}</span>
                <span>원천 count 재검증 {rtms.coverage.verification.sourceCountReverifiedCombinations}/{rtms.coverage.verification.eligibleCombinations}</span>
                <span>full-hash 재검증 {rtms.coverage.verification.sourceFullHashReverifiedCombinations}/{rtms.coverage.verification.eligibleCombinations}</span>
                {rtms.coverage.verification.dirtyCombinations > 0 && <span>dirty {rtms.coverage.verification.dirtyCombinations} · 분석 제외</span>}
              </> : null}
              {rtms.coverage.currentMonthProvisional && <span>당월 거래건수 불완전</span>}
              {rtms.coverage.latestFullCalendarMonth && <span>카드 기준월 {rtms.coverage.latestFullCalendarMonth}</span>}
              {rtms.coverage.revisionWindowMonths?.length ? <span>수정대기 {rtms.coverage.revisionWindowMonths.join(", ")}</span> : null}
            </div>

            <div className="housing-band-grid housing-band-grid--rtms">
              {rtmsLatest.map(({ band, row, countMom, priceMom }) => (
                <article className="housing-band-card" key={band.id}>
                  <span className="housing-band-card__index" style={{ borderBottom: `3px solid ${HOUSING_BAND_COLORS[band.id] ?? "#64748b"}` }}>RT</span>
                  <div>
                    <h4>{band.label}</h4>
                    <strong>{row ? `${row.transactionCount.toLocaleString()}건` : "N/A"}</strong>
                    <p>전체 거래 비중 {row ? `${row.transactionSharePct.toFixed(1)}%` : "N/A"} · 전월대비 건수 {signedPct(countMom)}</p>
                    <small>거래가격 proxy {eokFromKrw(row?.medianPriceProxyKrw ?? null)} · 전월 {signedPct(priceMom)}</small>
                  </div>
                </article>
              ))}
            </div>

            <div className="housing-grid">
              <TimeSeriesChart
                title={`가격대별 거래${rtmsMetric === "count" ? "건수" : "비중"}${rtmsSmoothing === "ma3" ? " · 3M 평균" : ""}`}
                subtitle="가격보다 거래 위축이 먼저 나타나는지 확인합니다. 최근 신고월은 수정될 수 있습니다."
                series={rtmsActivitySeries}
                range={range}
                height={310}
                syncId="housing-transmission"
                onOpenDetail={() => onOpenDetail({ title: `${HOUSING_SCOPE_LABELS[rtmsScope]} 가격대별 거래${rtmsMetric === "count" ? "건수" : "비중"}`, description: "국토교통부 실거래를 월·지역·가격대별로 집계합니다. 상세보기에서 기간, 변화량, Z-score, percentile, 드래그 확대와 CSV를 사용할 수 있습니다.", series: rtmsActivitySeries })}
              />
              <TimeSeriesChart
                title="가격대별 거래가격 방향 proxy"
                subtitle="동일주택 가격지수가 아니라 시군구 중앙가격의 거래건수 가중 proxy입니다."
                series={rtmsPriceSeries}
                range={range}
                height={310}
                syncId="housing-transmission"
                onOpenDetail={() => onOpenDetail({ title: `${HOUSING_SCOPE_LABELS[rtmsScope]} 가격대별 거래가격 proxy`, description: "거래 구성 변화가 가격 proxy에 섞일 수 있습니다. 동일주택 가격지수로 해석하지 않습니다.", series: rtmsPriceSeries })}
              />
            </div>

            <section className="housing-h25-section">
              <div className="housing-h2-header">
                <div>
                  <p className="kicker">H2.5 · PRIME / HIGH-END MARKET</p>
                  <h4>프라임 지역 · 고가시장 축</h4>
                  <p>정책구간과 별도로 30억·50억을 시장 경계로 보고, 60억 이상은 초고가 tail로 추적합니다. 기존 H2 정책구간 캐시는 그대로 유지합니다.</p>
                </div>
                <div className="housing-h2-controls">
                  <div className="housing-control-block">
                    <span>지역</span>
                    <div className="housing-profile-switch" role="group" aria-label="고가시장 지역범위">
                      {MARKET_SCOPE_ORDER.map((id) => (
                        <button key={id} type="button" className={marketScope === id ? "is-active" : ""} onClick={() => setMarketScope(id)}>{HOUSING_SCOPE_LABELS[id]}</button>
                      ))}
                    </div>
                  </div>
                  <div className="housing-control-block">
                    <span>표시</span>
                    <div className="housing-profile-switch" role="group" aria-label="고가시장 표시방식">
                      <button type="button" className={marketMetric === "count" ? "is-active" : ""} onClick={() => setMarketMetric("count")}>건수</button>
                      <button type="button" className={marketMetric === "share" ? "is-active" : ""} onClick={() => setMarketMetric("share")}>비중</button>
                    </div>
                  </div>
                  <div className="housing-control-block">
                    <span>평활화</span>
                    <div className="housing-profile-switch" role="group" aria-label="고가시장 평활화">
                      <button type="button" className={marketSmoothing === "raw" ? "is-active" : ""} onClick={() => setMarketSmoothing("raw")}>원자료</button>
                      <button type="button" className={marketSmoothing === "ma3" ? "is-active" : ""} onClick={() => setMarketSmoothing("ma3")}>3M 평균</button>
                    </div>
                  </div>
                </div>
              </div>

              {!market || market.status === "not_collected" ? (
                <div className="housing-h2-placeholder">
                  <strong>고가시장 별도 backfill: N/A</strong>
                  <span>기존 정책구간 데이터는 계속 사용하고, 30/50/60억 구간은 원 RTMS 응답을 다시 읽어 별도 캐시에 채웁니다.</span>
                  <small>기존 15/25억 캐시를 억지로 재분할하지 않으므로 과거 데이터 왜곡이 없습니다.</small>
                </div>
              ) : (
                <>
                  <div className="housing-h2-status">
                    <span className={`data-pill data-pill--${market.status === "ready" ? "ok" : "warn"}`}>{market.status.toUpperCase()}</span>
                    <span>고가시장 backfill {market.coverage.completedCombinations}/{market.coverage.expectedCombinations} 지역×월</span>
                    <span>선택지역 {HOUSING_SCOPE_LABELS[marketScope]}</span>
                    <span>시장구간 15억 / 30억 / 50억</span>
                    <span>60억+ tail 별도</span>
                  </div>

                  <div className="housing-prime-grid">
                    <article className="housing-prime-card"><span>강남3구 30억+</span><strong>{primeSnapshot ? `${primeSnapshot.gangnam30.toLocaleString()}건` : "N/A"}</strong><small>{primeSnapshot?.month ?? "완료월 없음"}</small></article>
                    <article className="housing-prime-card"><span>강남3구+용산 50억+</span><strong>{primeSnapshot ? `${primeSnapshot.prime50.toLocaleString()}건` : "N/A"}</strong><small>{primeSnapshot?.month ?? "완료월 없음"}</small></article>
                    <article className="housing-prime-card"><span>강남3구+용산 60억+</span><strong>{primeSnapshot ? `${primeSnapshot.prime60.toLocaleString()}건` : "N/A"}</strong><small>초고가 tail · {primeSnapshot?.month ?? "N/A"}</small></article>
                    <article className="housing-prime-card"><span>Prime 30억+ / 서울 전체</span><strong>{primeSnapshot?.primeSharePct == null ? "N/A" : `${primeSnapshot.primeSharePct.toFixed(1)}%`}</strong><small>거래 집중도 · {primeSnapshot?.month ?? "N/A"}</small></article>
                  </div>

                  <div className="housing-market-band-grid">
                    {marketLatest.bands.map(({ band, row, countMom, priceMom }) => {
                      const sample = row ? (row.transactionCount < 5 ? "N/A PRICE" : row.transactionCount < 10 ? "LOW SAMPLE" : "") : "";
                      return (
                        <article className="housing-band-card" key={band.id}>
                          <span className="housing-band-card__index" style={{ borderBottom: `3px solid ${HOUSING_BAND_COLORS[band.id] ?? "#64748b"}` }}>MK</span>
                          <div>
                            <h4>{band.label}</h4>
                            <strong>{row ? `${row.transactionCount.toLocaleString()}건` : "N/A"}</strong>
                            <p>비중 {row ? `${row.transactionSharePct.toFixed(1)}%` : "N/A"} · 전월 건수 {signedPct(countMom)}</p>
                            <small>가격 proxy {row && row.transactionCount >= 5 ? eokFromKrw(row.medianPriceProxyKrw) : "N/A"} · 전월 {signedPct(priceMom)}{sample ? ` · ${sample}` : ""}</small>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <div className="housing-grid">
                    <TimeSeriesChart
                      title={`${HOUSING_SCOPE_LABELS[marketScope]} 시장가격대별 거래${marketMetric === "count" ? "건수" : "비중"}${marketSmoothing === "ma3" ? " · 3M 평균" : ""}`}
                      subtitle="30억·50억 경계를 중심으로 프라임시장 유동성이 확산되는지 관찰합니다."
                      series={marketActivitySeries}
                      range={range}
                      height={310}
                      syncId="housing-prime-market"
                      onOpenDetail={() => onOpenDetail({ title: `${HOUSING_SCOPE_LABELS[marketScope]} 고가시장 거래`, description: "고가시장 별도 backfill 계열입니다. 상세보기에서 기간·변환·줌·CSV를 사용할 수 있습니다.", series: marketActivitySeries })}
                    />
                    <TimeSeriesChart
                      title={`${HOUSING_SCOPE_LABELS[marketScope]} 고가 거래가격 proxy`}
                      subtitle="월 5건 미만 구간은 N/A 처리하며 동일주택 가격지수가 아닙니다."
                      series={marketPriceSeries}
                      range={range}
                      height={310}
                      syncId="housing-prime-market"
                      onOpenDetail={() => onOpenDetail({ title: `${HOUSING_SCOPE_LABELS[marketScope]} 고가 거래가격 proxy`, description: "월 5건 미만은 가격값을 공개하지 않습니다. 5~9건은 표본이 얇으며 composition bias를 주의합니다.", series: marketPriceSeries })}
                    />
                  </div>

                  <p className="housing-h4-footnote">지역축: 강남3구(강남·서초·송파), 강남3구+용산, 서울 비강남3구. 가격축: ≤15억 / &gt;15~&lt;30억 / 30~&lt;50억 / ≥50억, 60억+는 중복 tail입니다. 지역·가격 집중을 곧바로 가격 선행성으로 해석하지 않습니다.</p>
                </>
              )}
            </section>

            <div className="housing-public-hts-link" data-export-hide="true">
              <div>
                <strong>공개 HTS 전용 화면</strong>
                <span>같은 계산·데이터를 전체 대시보드 없이 공유할 수 있습니다.</span>
              </div>
            </div>
            <PublicHousingHtsShareTools compact />

            <HousingMarketTechnicalChart officialHousingPrices={officialHousingPrices} />

            <section className="housing-h4-section">
              <header className="housing-h4-header">
                <div>
                  <p className="kicker">H4 · SYNCHRONIZED TRANSMISSION</p>
                  <h4>시장금리 → 조달금리 → 주담대 → 거래 → 가격</h4>
                  <p>세 패널의 월축과 crosshair를 동기화합니다. KTB5Y는 일별 관측치를 보간 없이 월평균하고, 금융채·주담대는 ECOS 월별 원계열을 사용합니다.</p>
                </div>
              </header>

              <TimeSeriesChart
                title="금리 전달 경로"
                subtitle="KTB5Y 월평균 → 금융채 신규조달 → 주담대 신규금리"
                series={housingRateSeries}
                range={range}
                height={250}
                syncId="housing-transmission"
                onOpenDetail={() => onOpenDetail({ title: "부동산 금리 전달 경로", description: "KTB5Y는 유효 일별 관측의 월평균, 금융채·주담대는 공식 월별 계열입니다.", series: housingRateSeries })}
              />

              <div className="housing-h4-grid">
                <TimeSeriesChart
                  title="가격대별 거래건수"
                  subtitle={`${HOUSING_SCOPE_LABELS[rtmsScope]} 실거래를 정책 가격구간으로 분리`}
                  series={rtmsCountSeries}
                  range={range}
                  height={240}
                  syncId="housing-transmission"
                  onOpenDetail={() => onOpenDetail({ title: `${HOUSING_SCOPE_LABELS[rtmsScope]} 가격대별 거래건수`, description: "정책구간 기준 월별 거래건수입니다.", series: rtmsCountSeries })}
                />
                <TimeSeriesChart
                  title="가격대별 거래가격 proxy"
                  subtitle="동일주택 가격지수가 아니며 composition bias가 있습니다."
                  series={rtmsPriceSeries}
                  range={range}
                  height={240}
                  syncId="housing-transmission"
                  onOpenDetail={() => onOpenDetail({ title: `${HOUSING_SCOPE_LABELS[rtmsScope]} 가격대별 거래가격 proxy`, description: "거래 구성 변화가 포함될 수 있는 방향성 proxy입니다.", series: rtmsPriceSeries })}
                />
              </div>
              <p className="housing-h4-footnote">금리·거래·가격 패널은 동일한 전역 기간 선택을 따르며 각각 상세보기에서 추가 확대·변환이 가능합니다. 거래량 감소 또는 가격 proxy 하락만으로 H4 Price Shock을 선언하지 않습니다.</p>
            </section>

            <section className="housing-h4-section">
              <header className="housing-h4-header">
                <div>
                  <p className="kicker">H4.4 · OFFICIAL HOUSING PRICE MAX</p>
                  <h4>공식 주택가격지수 · 가능한 최초시점부터</h4>
                  <p>한국부동산원 아파트 매매가격지수의 공식 기준시점 개편 vintage를 ECOS 메타데이터로 확인하고, 최신 vintage를 우선하면서 이전 역사만 겹치는 구 vintage로 연결한 연속계열입니다. KB·변동률·인접계열로 대체하지 않습니다.</p>
                </div>
              </header>
              {officialHousingPrices.length ? (
                <TimeSeriesChart
                  title="공식 아파트 매매가격지수"
                  subtitle="전국 / 수도권 / 서울 · 공식 vintage를 연결한 MAX 연속계열"
                  series={officialHousingPrices}
                  range={range}
                  height={280}
                  syncId="housing-official-price"
                  onOpenDetail={() => onOpenDetail({ title: "공식 아파트 매매가격지수 MAX", description: "한국은행 ECOS의 한국부동산원 공식 기준시점 개편 vintage를 메타데이터로 확인해 최신 vintage 구간을 우선하고, 그 이전 역사만 overlap ratio로 연결한 파생 연속지수입니다. 결측은 보간하지 않습니다.", series: officialHousingPrices })}
                />
              ) : <div className="empty-state">공식 가격지수 exact 계열을 아직 확정하지 못했거나 ECOS 응답이 없습니다. 인접 계열로 대체하지 않고 N/A를 유지합니다.</div>}
            </section>

            <details className="housing-advanced-analysis">
              <summary>
                <span><strong>고급 분석</strong><small>전이시차 · 방향성 · 가설검증 · 구조변화</small></span>
                <b>펼쳐보기</b>
              </summary>
              <div className="housing-advanced-analysis__body">
            <section className="housing-h4-section housing-lead-lag-section">
              <header className="housing-h4-header">
                <div>
                  <p className="kicker">H4.5 · TRANSMISSION WINDOWS</p>
                  <h4>금리 → 거래량 → 주택가격 전이구간</h4>
                  <p>각 결과변수가 실제로 존재하는 MAX 공통기간을 독립적으로 사용합니다. 주담대·시장·기준금리→공식 주택가격지수의 직접 관계와, 주담대→거래량→RTMS 가격 proxy의 간접 경로를 함께 봅니다. 기존 호환표는 즉시(0~2M) · 단기(3~6M) · 중기(7~12M)를 유지하지만, H4.7 방향성 판정에서는 0M을 동행으로 격리하고 1~2M부터 정방향/역방향을 비교합니다. RTMS 거래량은 계절성 방어를 위해 YoY 변화를 사용합니다.</p>
                </div>
              </header>
              <LagAnalysisTable
                scans={housingLagScans}
                emptyText="공식 가격지수 또는 RTMS 결과변수와 금리의 공통 표본이 충분해지면 0~12개월 전이구간을 자동 계산합니다."
              />
              <p className="housing-h4-footnote">+kM는 앞 지표가 k개월 먼저 관측된 조합이라는 뜻입니다. 0M은 같은 달 동행이라 선행으로 부르지 않습니다. H4.7에서 역방향 scan과 null envelope를 함께 확인한 뒤에만 전파 후보로 올립니다. 공식 가격지수는 주력 가격계열이고 RTMS 가격 proxy는 composition bias가 있는 보조계열입니다.</p>
            </section>

            <section className="housing-h4-section">
              <header className="housing-h4-header"><div><p className="kicker">MAX HISTORICAL COVERAGE</p><h4>얼마나 과거까지 실제로 확보됐나</h4><p>10Y는 보기 편한 프리셋일 뿐입니다. 수집 목표는 각 공식 소스의 가능한 최초시점→현재(MAX)이며, exact 역사경계가 검증되지 않은 소스는 완료로 가장하지 않습니다.</p></div></header>
              <div className="housing-structure-summary">
                {historicalCoverage.items.map((item) => (
                  <article key={item.id}>
                    <span>{item.provider}</span>
                    <strong>{item.label}</strong>
                    <p>{item.collectedStart ?? "N/A"} → {item.collectedEnd ?? "N/A"} · {item.status === "max_ready" ? "MAX 확보" : item.status === "partial" ? "부분 확보" : item.status === "blocked" ? "역사확장 차단" : "N/A"}</p>
                    {item.blocker && <small>⚠ {item.blocker}</small>}
                  </article>
                ))}
              </div>
              <p className="housing-h4-footnote">MAX 확보 {historicalCoverage.maxReady}/{historicalCoverage.total}. 오래된 지역코드·공식 아카이브가 정확히 확인되는 즉시 기존 분석을 고치지 않아도 자동으로 공통기간과 구조변화 탐색범위가 확장됩니다.</p>
            </section>

            <HousingRelationshipAnalysisPanel analysis={housingRelationshipAnalysis} />
            <TransmissionHypothesisPanel analysis={housingRelationshipAnalysis} />
              </div>
            </details>

            <div className="housing-interpretation-grid">
              <article><span className="housing-evidence housing-evidence--observed">관측</span><strong>실거래는 신고·해제 자료를 기준으로 가격대별로 분리됩니다.</strong><p>{HOUSING_SCOPE_LABELS[rtmsScope]}에서 SDP가 보관한 월별 아파트 매매를 ≤15억 / 15~25억 / &gt;25억으로 집계합니다. 최근 신고월의 수정분은 SDP 중앙 수집기가 반영하며, 이 화면은 중앙 데이터를 읽어 분석합니다.</p></article>
              <article><span className="housing-evidence housing-evidence--mechanism">메커니즘</span><strong>거래량 반응은 가격보다 먼저 나타날 수 있습니다.</strong><p>금리·대출규제가 매수 가능성을 낮추면 먼저 거래가 줄고, 가격은 매물구조·기대·희소성 때문에 뒤늦게 반응할 수 있습니다.</p></article>
              <article><span className="housing-evidence housing-evidence--inference">해석</span><strong>거래 감소만으로 가격하락을 선언하지 않습니다.</strong><p>{rtms.quality?.compositionWarning ?? "거래가격 변화에는 거래 구성 변화가 포함될 수 있으므로 별도 가격지수와 함께 확인해야 합니다."}</p></article>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
